import { capitalizeFirstLetter, relevanceMapping } from '../helpers/filterNames';
import { Classification, Relevance } from '../types';

export function getExtensionLabel(extension: string): string {
  return extension.toUpperCase();
}

// Document type lookup with capitalization
export function getDocumentTypeLabel(documentType: string): string {
  if (!documentType || documentType === '') return 'Unassigned';

  return documentType
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export function getRelevanceLabel(relevance: Relevance): string {
  return relevanceMapping[relevance];
}

export function getClassificationLabel(classification: Classification): string {
  return capitalizeFirstLetter(classification);
}

// Person ID to name lookup using data from useGetPersons
export function getPersonLabel(personId: string, personsMap?: Map<string, string>): string {
  return personsMap?.get(personId) || personId;
}

// Boolean to display string
export function getBooleanLabel(value: boolean): string {
  return value ? 'Yes' : 'No';
}

// Processed status label
export function getProcessedLabel(processed: boolean): string {
  return processed ? 'Processed' : 'Not Processed';
}

// Date formatting for display
export function getDateLabel(dateString: string, prefix: 'From' | 'To' = 'From'): string {
  const date = new Date(dateString);
  const formatted = date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
  return `${prefix}: ${formatted}`;
}

// Utility to create a persons map from useGetPersons data
export function createPersonsMap(persons?: Array<{ id: string; name: string }>): Map<string, string> {
  if (!persons) return new Map();
  return new Map(persons.map((person) => [person.id, person.name]));
}

// Filter option type for UI components that still need {label, value} format
export interface FilterOption<T = string> {
  label: string;
  value: T;
}

// Convert raw arrays to FilterOption arrays for UI components that require this format
export function createExtensionOptions(extensions: string[]): FilterOption[] {
  return extensions.map((ext) => ({
    label: getExtensionLabel(ext),
    value: ext,
  }));
}

export function createDocumentTypeOptions(documentTypes: string[]): FilterOption[] {
  return documentTypes.map((type) => ({
    label: getDocumentTypeLabel(type),
    value: type,
  }));
}

export function createRelevanceOptions(relevanceValues: Relevance[]): FilterOption[] {
  return relevanceValues.map((rel) => ({
    label: getRelevanceLabel(rel),
    value: rel,
  }));
}

export function createClassificationOptions(classificationValues: Classification[]): FilterOption[] {
  return classificationValues.map((cls) => ({
    label: getClassificationLabel(cls),
    value: cls,
  }));
}

export function createPersonOptions(personIds: string[], personsMap: Map<string, string>): FilterOption[] {
  return personIds.map((id) => ({
    label: getPersonLabel(id, personsMap),
    value: id,
  }));
}
